/**
 * * PROMO ENGINE CLIENT API
 * lo scopo è quello di utilzzare in maniera autonoma, il file openapi  (api/api.ts)
 * senza passare da altri file del progetto (limitando l'incorporamento al minimo),
 * es: attualmente, per altri moduli, usiamo il file `axios.ts` src/addons/axios.ts, dove ci sono le API CORE.
 * questo porta ad un legame troppo stretto tra modulo e progetto, non gestibile in ottica modulare
 *
 * TODO: se il pattern funziona va riportato negli altri moduli ASAP
 */

// per attivare il MOCK aggiungere al proprio `.env.local`: VITE_PROMO_ENGINE_MOCK_API = 'true'
const VITE_PROMO_ENGINE_MOCK_API =
  import.meta.env.VITE_PROMO_ENGINE_MOCK_API === 'true' || false

import store from '@/store'
import I18n from '@/addons/i18n'
import { getAxiosConfigs, salesApi } from '@/addons/axios'
import { getPoswebOrigin, extractErrorMessage } from '@/addons/functions'

import {
  PromoEngineApiFactory,
  PromoEnginePreviewResponse,
  PromoEngineApplyResponse,
  GetPromoEnginePromotionListData,
  SalesData,
} from '@/api'

import type {
  InlineResponse2008,
  InlineResponse2006 as PromoV1EstimateResponse,
} from '@/api/api'

import { MANUAL_ESTIMATE_PAYLOAD, mockPromoEngineApi } from './_mock'
import { NotificationsActions } from '@/store/notifications-store'
import { ConfigGetters } from '@/store/configs-store'
import { ErrorD } from '@/addons/interface'

/**
 * Inizzializza istanza PromoEngineApi
 */
async function initializePromoEngineApi() {
  const basePath =
    store.getters[ConfigGetters.GET_SELECTED_CASH_URL] ||
    (await getPoswebOrigin())
  const configuration = getAxiosConfigs()
  return PromoEngineApiFactory(configuration, basePath)
}

/**
 * Gestione centralizzata degli errori API
 */
async function handleApiError(error: unknown): Promise<void> {
  const err = error as ErrorD

  let errorMessage = ''

  // Verifica se esiste una lista di errori nella response
  if (err?.response?.data?.errors?.length) {
    const details = extractErrorMessage(
      err?.response?.data?.errors?.[0]?.detail ??
        I18n.global.t('pos_common.generic_error'),
    )
    if (details) {
      errorMessage += `:${details}\n`
    }
  }

  if (errorMessage) {
    await store.dispatch(NotificationsActions.NOTIFY_ERROR, errorMessage)
  }
}

/** Error Handler no fallback */
// async function handleApiErrorNoFb(error: unknown): Promise<void> {
//   const err = error as ErrorD
//   const detail = err?.response?.data?.errors?.[0]?.detail
//   if (!detail) return
//   const errorMessage = extractErrorMessage(detail)
//   if (errorMessage) {
//     await store.dispatch(NotificationsActions.NOTIFY_ERROR, errorMessage)
//   }
// }

/**
 * Ottiene la lista delle promozioni - PromoEngine AUTO
 */
export const getPromotionsListAuto = async (): Promise<
  GetPromoEnginePromotionListData[]
> => {
  if (VITE_PROMO_ENGINE_MOCK_API) {
    const mockResponse = await mockPromoEngineApi.apiV1PromoEngineV2ListGet()
    return mockResponse.data.data || []
  }

  const apiInstance = await initializePromoEngineApi()
  try {
    const response = await apiInstance.apiV1PromoEngineV2ListGet()
    return response.data.data || []
  } catch (error: unknown) {
    await handleApiError(error)
    return []
  }
}

/**
 * Preview promozioni - PromoEngine AUTO
 */
export async function previewPromotionsAuto(
  payload: SalesData,
): Promise<PromoEnginePreviewResponse | undefined> {
  try {
    const response = await salesApi.apiV1PromoEngineV2PreviewPost(payload)
    return response.data
  } catch (error: unknown) {
    await handleApiError(error)
  }
}

/**
 * Applica promozioni - PromoEngine AUTO
 */
export async function applyPromotionsAuto(
  payload: SalesData,
): Promise<PromoEngineApplyResponse | undefined> {
  try {
    const response = await salesApi.apiV1PromoEngineV2ApplyPatch(payload)
    return response.data
  } catch (error: unknown) {
    await handleApiError(error)
  }
}

/**
 * Estimate promozioni - PromoEngine MANUAL
 */
export async function estimatePromotionsManual(
  payload: SalesData,
): Promise<PromoV1EstimateResponse | undefined> {
  try {
    if (VITE_PROMO_ENGINE_MOCK_API) {
      const response = await mockPromoEngineApi.apiV1AbstridPromoV1EstimatePost(
        {
          data: MANUAL_ESTIMATE_PAYLOAD,
        },
      )
      // console.log(response.data)
      return response.data
    }

    const apiInstance = await initializePromoEngineApi()
    const response = await apiInstance.apiV1AbstridPromoV1EstimatePost({
      data: payload,
    })
    return response.data
  } catch (error: unknown) {
    await handleApiError(error)
  }
}

/**
 * Apply (preview) promozioni - PromoEngine MANUAL (w/wo confirm param)
 * Se 'checkout' è valorizzato, il servizio salva lato promo-engine.
 * USO:
 * PREVIEW:  const resp = await PromoEngineClient.applyPromotionsManual(payload)
 * CHECKOUT: const resp = await PromoEngineClient.applyPromotionsManual(payload, '1')

 */
export async function applyPromotionsManual(
  payload: unknown,
  checkout?: string,
): Promise<PromoV1EstimateResponse | undefined> {
  try {
    if (VITE_PROMO_ENGINE_MOCK_API) {
      const response = await mockPromoEngineApi.apiV1AbstridPromoV1ApplyPost(
        checkout,
        { data: MANUAL_ESTIMATE_PAYLOAD }, // --> creare MOCK appropriato
      )
      return response.data
    }

    const apiInstance = await initializePromoEngineApi()
    const response = await apiInstance.apiV1AbstridPromoV1ApplyPost(checkout, {
      data: payload,
    })
    return response.data
  } catch (error: unknown) {
    await handleApiError(error)
  }
}

/**
 * NEW CONFIRM SESSION promozioni - PromoEngine MANUAL
 */
export async function confirmSession(
  payload: any,
): Promise<InlineResponse2008 | undefined> {
  try {
    const apiInstance = await initializePromoEngineApi()
    const response = await apiInstance.apiV1AbstridPromoV1ConfirmSessionPost({
      // data: JSON.stringify(payload),
      data: payload,
    })
    return response.data
  } catch (error) {
    await handleApiError(error)
  }
}

/**
 * Lista campagne - PromoEngine
 */
export const getCampaigns = async (
  filterRetailCode?: string,
  filterLifecycle?: string,
  filterActive?: 0 | 1,
  filterConditions?: 0 | 1,
  filterStartDate?: string,
  filterEndDate?: string,
  filterPage?: number,
  filterSize?: number,
  filterLanguage?: string,
  filterConsumerCode?: number,
  filterIsJob?: number,
  params?: string,
): Promise<any[]> => {
  if (VITE_PROMO_ENGINE_MOCK_API) {
    const mockResponse =
      await mockPromoEngineApi.apiV1AbstridPromoV1CampaignsGet(
        filterRetailCode,
        filterLifecycle,
        filterActive,
        filterConditions,
        filterStartDate,
        filterEndDate,
        filterPage,
        filterSize,
        filterLanguage,
        filterConsumerCode,
        filterIsJob,
        params,
      )
    return mockResponse.data.data || []
  }

  const apiInstance = await initializePromoEngineApi()
  try {
    const response = await apiInstance.apiV1AbstridPromoV1CampaignsGet(
      filterRetailCode,
      filterLifecycle,
      filterActive,
      filterConditions,
      filterStartDate,
      filterEndDate,
      filterPage,
      filterSize,
      filterLanguage,
      filterConsumerCode,
      filterIsJob,
      params,
    )
    return response.data.data || []
  } catch (error: unknown) {
    await handleApiError(error)
    return []
  }
}
